import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { PostService } from '../services/posts.services';
import { CommentsService } from '../../comments/services/comments.services';
import * as PostsActions from './posts.actions';
import { of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../shared/services/toast.service';

@Injectable()
export class PostsEffects {

  constructor(private actions$: Actions,
    private postsService: PostService,
    private commentsService: CommentsService,
    private toastService: ToastService,
    private translate: TranslateService) {}

  getPostsEffect$ = createEffect(() => this.actions$.pipe(
    ofType(PostsActions.getPosts),
    switchMap(() =>
      this.postsService.getPosts().pipe(
        map(posts => PostsActions.getPostsSuccess({posts}))
      )
    )
  ));

  getFeedsEffect$ = createEffect(() => this.actions$.pipe(
    ofType(PostsActions.getFeeds),
    switchMap(() =>
      this.postsService.getFeeds().pipe(
        map(posts => PostsActions.getFeedsSuccess({posts}))
      )
    )
  ));

  deletePostEffect$ = createEffect(() => this.actions$.pipe(
    ofType(PostsActions.deletePost),
    switchMap((action) =>
      this.postsService.deletePost(action.postId).pipe(
        map(_ => {
          this.toastService.Success(this.translate.instant("post.delete.deleted"), '');
          return PostsActions.deletePostSuccess({postId: action.postId});
        }),
        catchError(() => of(PostsActions.deletePostFailure({ error: 'Error deleting post' })))
      )
    )
  ));

  createPostEffect$ = createEffect(() => this.actions$.pipe(
    ofType(PostsActions.createPost),
    switchMap((action) =>
      this.postsService.createPost(action.content,
        action.publicPost,
        action.publishOnNostr,
        action.imageFile,
        action.videoLink,
        action.videoType,
        '',
        action.tags
        ).pipe(
        map((post) => {
          this.toastService.Success(this.translate.instant("post.create.created"), '');
          return PostsActions.createPostSuccess({post});
        }),
        catchError(() => of(PostsActions.createPostFailure({ error: 'Error creating post' })))
      )
    )
  ));

  updatePostEffect$ = createEffect(() => this.actions$.pipe(
    ofType(PostsActions.updatePost),
    switchMap((action) =>
      this.postsService.updatePost(action.id,
        action.content,
        action.publicPost,
        action.imageFile,
        action.publishOnNostr,
        action.currentImageFile,
        action.videoLink,
        action.videoType,
        action.tags
        ).pipe(
        map((post) => {
          this.toastService.Success(this.translate.instant("post.edit.updated"), '');
          return PostsActions.updatePostSuccess({post})
        }),
        catchError(() => of(PostsActions.updatePostFailure({ error: 'Error updating post' })))
      )
    )
  ));

  addNewCommentEffect$ = createEffect(() => this.actions$.pipe(
    ofType(PostsActions.addComment),
    switchMap((action) =>
      this.commentsService.createComment(action.postId, action.comment).pipe(
        mergeMap((post) => [
          PostsActions.addCommentSuccess({post}),
          PostsActions.showNewComment('', false)
        ]),
        catchError(() => of(PostsActions.addCommentFailure({ error: 'Error adding comment' })))
      )
    )
  ));

  addNewCommentSuccessEffect$ = createEffect(() => this.actions$.pipe(
    ofType(PostsActions.addCommentSuccess),
    tap(() => {
      this.toastService.Success(this.translate.instant("comment.create.created"), '')
    }),
    ),
    { dispatch: false }
  );

  updateCommentEffect$ = createEffect(() => this.actions$.pipe(
    ofType(PostsActions.updateComment),
    switchMap((action) =>
      this.commentsService.editComment(action.comment.id, action.comment.content).pipe(
        mergeMap(post => [
          PostsActions.updateCommentSuccess({post}),
          PostsActions.showEditComment(false)
        ]),
        catchError(() => of(PostsActions.addCommentFailure({ error: 'Error updating comment' })))
      )
    )
  ));

  updateNewCommentSuccessEffect$ = createEffect(() => this.actions$.pipe(
    ofType(PostsActions.updateCommentSuccess),
    tap((action) => {
      this.toastService.Success(this.translate.instant("comment.edit.changed"), '')
    }),
    map(() => {
      return { type: '' };
    })
  ));

  deleteCommentEffect$ = createEffect(() => this.actions$.pipe(
    ofType(PostsActions.deleteComment),
    switchMap((action) =>
      this.commentsService.deleteComment(action.postId).pipe(
        map(post => {
          this.toastService.Success(this.translate.instant("comment.delete.deleted"), '')
          return PostsActions.deleteCommentSuccess({post})
        }),
        catchError(() => of(PostsActions.addCommentFailure({ error: 'Error deleting comment' })))
      )
    )
  ));

}
